/*
 * Alwan coverage calculator: pure calculation, no DOM.
 * All lengths are centimetres unless a name says otherwise.
 * Loaded in the browser as window.AlwanCoverage and in Node via require() for tests.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.AlwanCoverage = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // Site measurements are rarely better than half a centimetre, so a wall that is
  // 0.5 cm longer than a whole number of pieces does not trigger an extra cut column.
  var EPS = 0.5;
  var MAX_SURFACE_CM = 10000;
  var MAX_ITEM_CM = 10000;

  // ---------------------------------------------------------------------------
  // Input parsing
  // ---------------------------------------------------------------------------

  function normalizeDigits(text) {
    return String(text == null ? '' : text)
      .replace(/[٠-٩]/g, function (d) { return String(d.charCodeAt(0) - 0x0660); })
      .replace(/[۰-۹]/g, function (d) { return String(d.charCodeAt(0) - 0x06F0); })
      .replace(/٫/g, '.')
      .replace(/٬/g, ',');
  }

  // Accepts "3.5", "3,5", "٣٫٥" and "٣٥٠". Returns NaN for anything else.
  function parseNumber(value) {
    if (typeof value === 'number') return value;
    var s = normalizeDigits(value).replace(/\s+/g, '').replace(/,/g, '.');
    if (!/^(\d+\.?\d*|\.\d+)$/.test(s)) return NaN;
    return parseFloat(s);
  }

  function toCm(value, unit) {
    var n = parseNumber(value);
    if (!isFinite(n)) return NaN;
    return unit === 'm' ? n * 100 : n;
  }

  var CM_PER_UNIT = {
    'mm': 0.1, 'millimeter': 0.1, 'millimeters': 0.1, 'millimetre': 0.1, 'millimetres': 0.1, 'مم': 0.1, 'ملم': 0.1, 'مليمتر': 0.1, 'مليمترات': 0.1, 'ملليمتر': 0.1, 'ملليمترات': 0.1,
    'cm': 1, 'centimeter': 1, 'centimeters': 1, 'centimetre': 1, 'centimetres': 1, 'سم': 1, 'سنتيمتر': 1, 'سنتيمترات': 1,
    'm': 100, 'meter': 100, 'meters': 100, 'metre': 100, 'metres': 100, 'م': 100, 'متر': 100, 'أمتار': 100, 'امتار': 100,
    'in': 2.54, 'inch': 2.54, 'inches': 2.54, 'بوصة': 2.54, 'بوصات': 2.54, 'إنش': 2.54, 'انش': 2.54,
    'ft': 30.48, 'foot': 30.48, 'feet': 30.48, 'قدم': 30.48, 'أقدام': 30.48, 'اقدام': 30.48,
    'yd': 91.44, 'yard': 91.44, 'yards': 91.44, 'ياردة': 91.44, 'ياردات': 91.44
  };

  function measurementToCm(measurement) {
    if (!measurement || measurement.value == null) return null;
    var unit = String(measurement.unit || "").toLowerCase().trim();
    var factor = CM_PER_UNIT[unit];
    if (typeof factor !== "number") return null;
    var cm = parseNumber(measurement.value) * factor;
    return isFinite(cm) && cm > 0 ? cm : null;
  }

  // Reads item dimensions from Arabic product copy such as
  // "المقاس: 77 سم × 70 سم", "122 × 280 سم", "عرض اللوحه : 20 سم ارتفاع اللوح: 280 سم",
  // "60 سم * 10 متر" or "بعرض 2 متر". Returns { widthCm, heightCm } (either may be null) or null.
  var NUM = '(\\d+(?:\\.\\d+)?)';
  // "م" and "متر" must not be the start of a longer Arabic word (مقاس، ملم، مربع…).
  var UNIT = '(سم|cm|سنتيمتر|سنتي|متر|م)(?![\\u0621-\\u064A])';
  var WIDTH_RE = new RegExp('عرض[^\\d\\n]{0,20}?' + NUM + '\\s*' + UNIT);
  var HEIGHT_RE = new RegExp('(?:[اأإ]رتفاع|طول)[^\\d\\n]{0,20}?' + NUM + '\\s*' + UNIT);
  var PAIR_RE = new RegExp(NUM + '\\s*(?:' + UNIT + ')?\\s*[×xX*✕]\\s*' + NUM + '\\s*(?:' + UNIT + ')?', 'g');

  function unitToCm(value, unit) {
    var n = parseFloat(value);
    return unit === 'متر' || unit === 'م' ? n * 100 : n;
  }

  function sane(cm) {
    return isFinite(cm) && cm >= 1 && cm <= MAX_ITEM_CM ? Math.round(cm * 10) / 10 : null;
  }

  function parseDimensions(text) {
    var s = normalizeDigits(text).replace(/ /g, ' ');
    var w = s.match(WIDTH_RE);
    var h = s.match(HEIGHT_RE);
    if (w && h) {
      return { widthCm: sane(unitToCm(w[1], w[2])), heightCm: sane(unitToCm(h[1], h[2])) };
    }
    // First size with a unit wins; a bare "60*60" in a title must not hide "60 سم × 60 سم" later.
    // A bare pair of small decimals ("1.20 × 2.80") is metres.
    var p, metres = null;
    PAIR_RE.lastIndex = 0;
    while ((p = PAIR_RE.exec(s))) {
      if (p[2] || p[4]) {
        var secondUnit = p[4] || p[2];
        var firstUnit = p[2] || secondUnit;
        return { widthCm: sane(unitToCm(p[1], firstUnit)), heightCm: sane(unitToCm(p[3], secondUnit)) };
      }
      if (!metres && /\./.test(p[1] + p[3]) && parseFloat(p[1]) <= 10 && parseFloat(p[3]) <= 10) {
        metres = { widthCm: sane(parseFloat(p[1]) * 100), heightCm: sane(parseFloat(p[3]) * 100) };
      }
    }
    if (metres) return metres;
    if (w) return { widthCm: sane(unitToCm(w[1], w[2])), heightCm: null };
    return null;
  }

  // ---------------------------------------------------------------------------
  // Surfaces
  // ---------------------------------------------------------------------------

  // mode: 'wall' | 'walls' | 'floor'
  // walls mode uses a rectangular room; walls[0] and walls[2] run along the length,
  // walls[1] and walls[3] along the width.
  function buildSurfaces(input) {
    var mode = input.mode || 'wall';
    if (mode === 'wall') {
      return [{ id: 'wall', w: input.widthCm, h: input.heightCm, horizontal: false }];
    }
    if (mode === 'walls') {
      var picks = input.walls || [true, true, true, true];
      var out = [];
      for (var i = 0; i < 4; i++) {
        if (!picks[i]) continue;
        out.push({ id: 'wall-' + i, w: i % 2 === 0 ? input.lengthCm : input.roomWidthCm, h: input.heightCm, horizontal: false });
      }
      return out;
    }
    return [{ id: mode, w: input.lengthCm, h: input.roomWidthCm, horizontal: true }];
  }

  // ---------------------------------------------------------------------------
  // Layouts
  // ---------------------------------------------------------------------------

  function along(length, size) {
    var full = Math.floor((length + EPS) / size);
    var rem = length - full * size;
    if (rem <= EPS) rem = 0;
    return { full: full, rem: Math.round(rem * 10) / 10, count: full + (rem > 0 ? 1 : 0) };
  }

  function sliverTip(rem, size) {
    if (!rem || rem >= Math.max(size * 0.25, 5)) return null;
    return { edgeCm: Math.round(rem * 10) / 10, centredEdgeCm: Math.round(((size + rem) / 2) * 10) / 10 };
  }

  // Pieces for one surface laid in a straight grid starting from one corner.
  // When the pattern must continue, every cut position takes a fresh piece.
  // Otherwise offcuts from the cut column/row are reused within the same surface.
  function tileSurface(surface, itemW, itemH, patternMatch) {
    var cols = along(surface.w, itemW);
    var rows = along(surface.h, itemH);
    var positions = cols.count * rows.count;
    var pieces = positions;

    if (!patternMatch && (cols.rem || rows.rem)) {
      pieces = cols.full * rows.full;
      if (cols.rem && rows.full) {
        pieces += Math.ceil(rows.full / Math.max(1, Math.floor((itemW + EPS) / cols.rem)));
      }
      if (rows.rem && cols.full) {
        pieces += Math.ceil(cols.full / Math.max(1, Math.floor((itemH + EPS) / rows.rem)));
      }
      if (cols.rem && rows.rem) pieces += 1;
    }

    return {
      id: surface.id,
      w: surface.w,
      h: surface.h,
      itemW: itemW,
      itemH: itemH,
      cols: cols,
      rows: rows,
      positions: positions,
      fullPieces: cols.full * rows.full,
      cutPositions: positions - cols.full * rows.full,
      pieces: pieces,
      sliverW: sliverTip(cols.rem, itemW),
      sliverH: sliverTip(rows.rem, itemH)
    };
  }

  // Whole pieces that are guaranteed to sit fully inside an opening, whatever
  // the grid offset. Deliberately conservative: small openings save nothing.
  function piecesInsideOpening(opening, itemW, itemH) {
    var across = Math.max(0, Math.floor((opening.w + EPS) / itemW) - 1);
    var up = Math.max(0, Math.floor((opening.h + EPS) / itemH) - 1);
    return across * up * (opening.count || 1);
  }

  function tileLayout(surfaces, item, opts) {
    var per = surfaces.map(function (s) { return tileSurface(s, item.w, item.h, opts.patternMatch); });
    var gross = per.reduce(function (sum, s) { return sum + s.pieces; }, 0);
    var saved = (opts.openings || []).reduce(function (sum, o) { return sum + piecesInsideOpening(o, item.w, item.h); }, 0);
    saved = Math.min(saved, Math.max(0, gross - per.length));
    return { surfaces: per, gross: gross, saved: saved, base: gross - saved };
  }

  // Wallpaper and sticker rolls: each surface is covered with vertical drops of the
  // roll width. Drop length is wall height plus trim, rounded up to the pattern repeat.
  // Drops are packed into rolls first-fit-decreasing, so mixed wall heights share rolls.
  function rollLayout(surfaces, item, opts) {
    var trim = opts.trimCm == null ? 10 : opts.trimCm;
    var repeat = opts.patternMatch && opts.repeatCm > 0 ? opts.repeatCm : 0;
    var drops = [];
    var per = surfaces.map(function (s) {
      var cols = along(s.w, item.w);
      var len = s.h + trim;
      if (repeat) len = Math.ceil(len / repeat) * repeat;
      for (var i = 0; i < cols.count; i++) drops.push(len);
      return { id: s.id, w: s.w, h: s.h, itemW: item.w, itemH: len, cols: cols, rows: { full: 1, rem: 0, count: 1 }, drops: cols.count, dropCm: len, sliverW: sliverTip(cols.rem, item.w), sliverH: null };
    });

    var tooLong = drops.some(function (d) { return d > item.h + EPS; });
    if (tooLong) return { error: 'roll_too_short', surfaces: per };

    drops.sort(function (a, b) { return b - a; });
    var rolls = [];
    drops.forEach(function (d) {
      for (var i = 0; i < rolls.length; i++) {
        if (rolls[i] + EPS >= d) { rolls[i] -= d; return; }
      }
      rolls.push(item.h - d);
    });

    var dropsPerRoll = per.length ? Math.floor((item.h + EPS) / per[0].dropCm) : 0;
    return { surfaces: per, gross: rolls.length, saved: 0, base: rolls.length, drops: drops.length, dropsPerRoll: dropsPerRoll };
  }

  // Floor coverings sold by running length from a roll of fixed width.
  // Tries strips in both directions and keeps the shorter total.
  function floorRollLayout(surfaces, item, opts) {
    var trim = opts.trimCm == null ? 5 : opts.trimCm;
    var step = opts.saleStepCm || 100;
    var s = surfaces[0];
    function run(across, length) {
      var strips = along(across, item.w).count;
      return { strips: strips, stripCm: length + trim, totalCm: strips * (length + trim) };
    }
    var a = run(s.h, s.w);
    var b = run(s.w, s.h);
    // a: strips run along the surface width (stacked across its height); b: the other way.
    var alongWidth = !(b.totalCm < a.totalCm);
    var best = alongWidth ? a : b;
    var base = Math.ceil((best.totalCm - EPS) / step);
    return { surfaces: [{ id: s.id, w: s.w, h: s.h, strips: best.strips, stripCm: best.stripCm, itemW: item.w, alongWidth: alongWidth }], gross: base, saved: 0, base: base, strips: best.strips, stripCm: best.stripCm, totalCm: best.totalCm };
  }

  function areaLayout(surfaces, item, opts) {
    var gross = surfaces.reduce(function (sum, s) { return sum + s.w * s.h; }, 0);
    var open = (opts.openings || []).reduce(function (sum, o) { return sum + o.w * o.h * (o.count || 1); }, 0);
    var net = Math.max(0, gross - Math.min(open, gross));
    var per = item.coverageM2 * 10000;
    return { surfaces: surfaces.map(function (s) { return { id: s.id, w: s.w, h: s.h }; }), gross: Math.ceil(gross / per - 1e-9), saved: 0, base: Math.ceil(net / per - 1e-9) };
  }

  // ---------------------------------------------------------------------------
  // Public entry point
  // ---------------------------------------------------------------------------

  function validLength(v, max) {
    return isFinite(v) && v > 0 && v <= max;
  }

  /*
   * input = {
   *   type: 'tile' | 'roll' | 'floor_roll' | 'area',
   *   surfaces: [{ id, w, h, horizontal }],         // from buildSurfaces
   *   item: { w, h, coverageM2 },                   // tile w×h, roll width×length, area coverage
   *   patternMatch, lockOrientation, repeatCm, trimCm, saleStepCm,
   *   openings: [{ w, h, count }],
   *   sparePct
   * }
   */
  function calculate(input) {
    var type = input.type || 'tile';
    var surfaces = (input.surfaces || []).filter(Boolean);
    var item = input.item || {};
    var openings = (input.openings || []).filter(function (o) { return o && o.count > 0 && o.w > 0 && o.h > 0; });

    if (!surfaces.length) return { ok: false, error: 'no_surface' };
    for (var i = 0; i < surfaces.length; i++) {
      if (!validLength(surfaces[i].w, MAX_SURFACE_CM) || !validLength(surfaces[i].h, MAX_SURFACE_CM)) {
        return { ok: false, error: 'surface_size' };
      }
    }
    if (type === 'area') {
      if (!(item.coverageM2 > 0)) return { ok: false, error: 'item_size' };
    } else if (!validLength(item.w, MAX_ITEM_CM) || (type !== 'floor_roll' && !validLength(item.h, MAX_ITEM_CM))) {
      return { ok: false, error: 'item_size' };
    }

    var opts = {
      patternMatch: !!input.patternMatch,
      repeatCm: input.repeatCm || 0,
      trimCm: input.trimCm,
      saleStepCm: input.saleStepCm,
      openings: openings
    };

    var layout;
    var alternative = null;
    if (type === 'roll') {
      layout = rollLayout(surfaces, item, opts);
      if (layout.error) return { ok: false, error: layout.error, layout: layout };
    } else if (type === 'floor_roll') {
      layout = floorRollLayout(surfaces, item, opts);
    } else if (type === 'area') {
      layout = areaLayout(surfaces, item, opts);
    } else {
      layout = tileLayout(surfaces, item, opts);
      var horizontal = surfaces.every(function (s) { return s.horizontal; });
      if (item.w !== item.h && (!input.lockOrientation || horizontal)) {
        var rotated = tileLayout(surfaces, { w: item.h, h: item.w }, opts);
        if (rotated.base < layout.base) {
          if (horizontal) {
            layout = rotated;
            layout.autoRotated = true;
          } else {
            alternative = { rotated: true, base: rotated.base };
          }
        }
      }
    }

    var sparePct = Math.max(0, input.sparePct || 0);
    var spare = layout.base > 0 && sparePct > 0 ? Math.ceil((layout.base * sparePct) / 100 - 1e-9) : 0;

    var grossCm2 = surfaces.reduce(function (sum, s) { return sum + s.w * s.h; }, 0);
    var openCm2 = openings.reduce(function (sum, o) { return sum + o.w * o.h * o.count; }, 0);
    var naive = null;
    if (type === 'tile') {
      naive = Math.ceil(Math.max(0, grossCm2 - openCm2) / (item.w * item.h) - 1e-9);
    }

    return {
      ok: true,
      type: type,
      base: layout.base,
      spare: spare,
      quantity: layout.base + spare,
      saved: layout.saved || 0,
      layout: layout,
      alternative: alternative,
      naiveByArea: naive,
      grossAreaM2: grossCm2 / 10000,
      netAreaM2: Math.max(0, grossCm2 - openCm2) / 10000
    };
  }

  return {
    normalizeDigits: normalizeDigits,
    parseNumber: parseNumber,
    toCm: toCm,
    measurementToCm: measurementToCm,
    parseDimensions: parseDimensions,
    buildSurfaces: buildSurfaces,
    calculate: calculate
  };
});
